import { Box, Stack, Typography } from '@mui/material';
import React from 'react';

import { colors } from '../../theme/theme';
import { ContractSnapshotResponse } from '../IMessageBubbleInterface';
import { formatCurrency } from '../MessageBubbleHelpers';

export const ContractSnapshotCard: React.FC<{ contractSnapshot: ContractSnapshotResponse | null }> = ({
    contractSnapshot,
}) => {
    if (!contractSnapshot) return null;

    const maybeCurrency = (value: string) => {
        const normalized = (value || '').trim();
        if (!normalized || normalized === '-') {
            return '-';
        }
        return /^\$?\s*[-]?[\d,.]+$/.test(normalized) ? formatCurrency(normalized) : normalized;
    };

    return (
        <Box>
            <Typography variant="body2" sx={{ mb: 1, color: colors.fullBlack, fontWeight: 700, fontSize: '0.95rem' }}>
                {contractSnapshot.intro}
            </Typography>

            <Stack gap={1}>
                {contractSnapshot.items.map(item => (
                    <Box
                        key={item.contractId}
                        sx={{
                            border: `1px solid ${colors.greyPreset[20]}`,
                            borderRadius: '12px',
                            backgroundColor: colors.fullWhite,
                            boxShadow: '0 1px 8px rgba(16, 24, 40, 0.05)',
                            p: 1,
                        }}
                    >
                        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.8 }}>
                            <Typography variant="body2" sx={{ color: colors.fullBlack, fontWeight: 700 }}>
                                Contract {item.contractId}
                            </Typography>
                            <Typography
                                variant="caption"
                                sx={{
                                    px: 0.8,
                                    py: 0.2,
                                    borderRadius: '999px',
                                    backgroundColor: colors.greyPreset[90],
                                    color: colors.greyPreset[60],
                                    fontWeight: 700,
                                }}
                            >
                                {item.status || '-'}
                            </Typography>
                        </Stack>

                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: {
                                    xs: 'repeat(2, minmax(0, 1fr))',
                                    sm: 'repeat(3, minmax(0, 1fr))',
                                },
                                gap: 0.75,
                            }}
                        >
                            {[
                                ['Asset', item.assetDesc],
                                ['Account Type', item.accountType],
                                ['EMI', maybeCurrency(item.emiAmount)],
                                ['Outstanding', maybeCurrency(item.outstandingBalance)],
                                ['Past Due', maybeCurrency(item.pastDue)],
                                ['Next Date', item.nextPaymentDate],
                                ['Next Amount', maybeCurrency(item.nextPaymentAmount)],
                                ['Autopay', item.autoPay],
                                ['Progress', item.paymentsProgress],
                                ['Maturity', item.maturityDate],
                            ].map(([label, value]) => (
                                <Box
                                    key={`${item.contractId}-${label}`}
                                    sx={{
                                        border: `1px solid ${colors.greyPreset[30]}`,
                                        borderRadius: '8px',
                                        backgroundColor: colors.greyPreset[5],
                                        px: 0.85,
                                        py: 0.65,
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: colors.greyPreset[50],
                                            fontWeight: 700,
                                            display: 'block',
                                            textTransform: 'uppercase',
                                        }}
                                    >
                                        {label}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: colors.fullBlack,
                                            fontWeight: 700,
                                            lineHeight: 1.35,
                                            wordBreak: 'break-word',
                                        }}
                                    >
                                        {value || '-'}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                ))}
            </Stack>
        </Box>
    );
};
